import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle, XCircle } from 'lucide-react';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const faqData = [
  { question: 'How long does it take to build a Revenue OS in-house?', answer: 'Most teams estimate 6-12 months for a basic version, but ongoing maintenance, data integration complexity, and AI model training push real timelines to 18-24 months before it matches a purpose-built solution like Forsyt.' },
  { question: 'What does it cost to build vs buy a Revenue OS?', answer: 'Building in-house typically requires 3-5 engineers full-time ($500K-$1.2M/year in salaries alone), plus data infrastructure costs. Forsyt delivers the same capabilities at a fraction of that cost with faster time-to-value.' },
  { question: 'Can Forsyt integrate with our existing tools?', answer: 'Yes. Forsyt sits on top of your CRM (Salesforce, Zoho, LeadSquared), call intelligence, email, and Slack. No rip-and-replace required.' },
];

const comparisonData = [
  { capability: 'Time to first value', buy: '2-4 weeks', build: '6-18 months' },
  { capability: 'Cross-tool data unification', buy: 'Built-in connectors', build: 'Custom ETL pipelines' },
  { capability: 'AI-driven action prescriptions', buy: 'Trained on 200+ CRO conversations', build: 'Train from scratch' },
  { capability: 'Ongoing maintenance', buy: 'Managed by Forsyt', build: '1-2 engineers full-time' },
  { capability: 'Rep adoption', buy: 'Purpose-built UX, walkthrough onboarding', build: 'Internal tool UX debt' },
  { capability: 'Cost (Year 1)', buy: 'Predictable SaaS pricing', build: '$500K-$1.2M+ engineering' },
  { capability: 'Iteration speed', buy: 'Weekly feature releases', build: 'Competes with product roadmap' },
];

const BuyVsBuild = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Buy vs Build a Revenue OS - Why GTM Teams Choose Forsyt"
        description="Should you build a Revenue OS in-house or buy Forsyt? Compare timelines, costs, and outcomes to make the right decision for your revenue team."
        path="/buy-vs-build"
        faq={faqData}
      />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Link to="/">
                <img
                  src="/assets/gt687tca.png"
                  alt="Forsyt"
                  className="h-8 w-auto"
                />
              </Link>
            </div>
            <Link to="/" className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-8" data-testid="buy-vs-build-title">
            Buy vs Build: The Revenue OS Decision Every CRO Faces
          </h1>

          <div className="prose prose-lg max-w-none">
            <section className="mb-12">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Every growing revenue team eventually asks: should we build our own action layer on top of our CRM, or buy a purpose-built Revenue OS? The instinct to build is natural, especially when you have engineering talent. But the math rarely works in your favor.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Here is what we have learned from speaking to 200+ CROs and revenue leaders about why most teams that start building end up buying.
              </p>
            </section>

            {/* Comparison Table */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Head-to-Head Comparison</h2>
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full" data-testid="comparison-table">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left py-4 px-6 text-sm font-bold text-gray-500 uppercase tracking-wider">Capability</th>
                      <th className="text-left py-4 px-6 text-sm font-bold text-blue-600 uppercase tracking-wider">Buy (Forsyt)</th>
                      <th className="text-left py-4 px-6 text-sm font-bold text-gray-500 uppercase tracking-wider">Build In-House</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonData.map((row, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="py-4 px-6 text-sm font-semibold text-gray-900">{row.capability}</td>
                        <td className="py-4 px-6 text-sm text-gray-700">
                          <span className="inline-flex items-center gap-1.5">
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                            {row.buy}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-500">
                          <span className="inline-flex items-center gap-1.5">
                            <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                            {row.build}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">The Hidden Cost of Building</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                The initial build is only 20% of the effort. The remaining 80% is data normalization across tools, maintaining integrations as APIs change, training AI models on your specific sales motions, and iterating on rep adoption. Most internal tools plateau at "dashboard that nobody opens" because the team that built it gets pulled to other priorities.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Forsyt is purpose-built to solve this. Our AI is already trained on 200+ CRO conversations. Our integrations are maintained by a dedicated team. And our UX is designed for rep adoption from day one, not as an afterthought.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">When Building Makes Sense</h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                If your GTM motion is genuinely unlike any other company, if you have a dedicated internal tools team with no competing priorities, and if you are willing to commit 18+ months before seeing ROI, building can work. For everyone else, buying Forsyt and deploying in weeks is the faster path to revenue impact.
              </p>
            </section>

            {/* FAQ */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">FAQ</h2>
              <div className="space-y-6">
                {faqData.map((item, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-xl p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{item.question}</h3>
                    <p className="text-gray-700">{item.answer}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* CTA */}
            <section className="text-center py-12 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">See Forsyt in Action</h2>
              <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                Skip the 18-month build cycle. See how Forsyt prescribes the exact next action for every rep, every deal, every day.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/dashboard"
                  className="inline-flex items-center px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-all"
                  data-testid="buy-vs-build-cta"
                >
                  Try the Sandbox
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
                <a
                  href="https://calendar.app.google/nswB9pt6ALLZ8xoy7"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-8 py-3 border-2 border-gray-300 hover:border-blue-600 text-gray-700 font-semibold rounded-lg transition-all"
                >
                  Book a Call
                </a>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BuyVsBuild;
